import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { getText } from 'i18n/intl';
import ObjectGenerationDrawer from 'pages/configuration/objectGeneration/ObjectGenerationDrawer';
import { fetchFields } from 'slices/field.slice';
import { fetchMemberships } from 'slices/memberships.slice';
import {
  fetchActivityCreateTemplates,
  fetchTemplates,
  getListIdSelected,
  requestDeleteTemplate,
  selectActivityCreateTemplates,
  selectAllObjectGenerationTemplates,
  selectFirstDrawer,
  selectTemplatesLoading,
  updateDrawerTemplate,
} from 'slices/objectGenerationTemplate.slice';
import {
  requestObjectTypes,
  selectObjectTypesList,
} from 'slices/objectTypes.slice';
import { fetchRelationTypes } from 'slices/relationType.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { EActivityDraftGroupConflictModel } from '@timeedit/types/lib/types/activityGroup.type';
import {
  Button,
  DefaultContentLayout,
  EditableTable,
} from '@timeedit/ui-components';

export const conflictModelsAlreadyImplemented: EActivityDraftGroupConflictModel[] =
  [EActivityDraftGroupConflictModel.PER_TRACK];

const Table = memo(EditableTable);
const ObjectGenerationPage = () => {
  const loading = useAppSelector(selectTemplatesLoading);
  const templates = useAppSelector(selectAllObjectGenerationTemplates);
  const firstDrawer = useAppSelector(selectFirstDrawer);
  const highlightIdsSelected = useAppSelector(getListIdSelected);
  const activityCreateTemplates = useAppSelector(selectActivityCreateTemplates);
  const dispatch = useAppDispatch();
  const objectTypes = useAppSelector(selectObjectTypesList);
  const columns = useMemo(() => {
    return [
      {
        title: getText('name'),
        dataIndex: 'name',
        key: 'name',
        width: 250,
        sorter: (a: any, b: any) => a.name.localeCompare(b.name),
      },
      {
        title: getText('objects_type'),
        dataIndex: 'objectType',
        key: 'objectType',
        width: 250,
        sorter: (a: any, b: any) => a.objectType.localeCompare(b.objectType),
        render: (val) => {
          const objectType = objectTypes.find((obj) => obj.id === val);
          return objectType ? objectType.name : val;
        },
      },
      {
        title: getText('conflict_model'),
        dataIndex: 'conflictModel',
        key: 'conflictModel',
        width: 250,
        render: (val) => {
          return getText(`CONFLICT_MODEL_${val}`);
        },
        sorter: (a: any, b: any) =>
          a.conflictModel.localeCompare(b.conflictModel),
      },
      {
        title: getText('activity_template'),
        dataIndex: 'activityCreateTemplateId',
        key: 'activityCreateTemplateId',
        width: 250,
        render: (val) => {
          const activityTemplate = activityCreateTemplates.find(
            (act) => act._id === val
          );
          return activityTemplate ? activityTemplate.name : val;
        },
        sorter: (a: any, b: any) => {
          const nameA =
            activityCreateTemplates.find(
              (act) => act._id === a.activityCreateTemplateId
            )?.name || '';
          const nameB =
            activityCreateTemplates.find(
              (act) => act._id === b.activityCreateTemplateId
            )?.name || '';
          return nameA.localeCompare(nameB);
        },
      },
    ];
  }, [activityCreateTemplates, objectTypes]);
  const onClickAddNew = useCallback(() => {
    // Create a new empty template object
    const newTemplate = {
      id: null, // or generate a temporary ID
      _id: '',
      name: '',
      objectType: 1,
      conflictModel: conflictModelsAlreadyImplemented[0],
      activityCreateTemplateId: '',
      fields: [],
      extIdFillWith: [],
      isEdited: false,
      relations: [],
      memberships: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Open the drawer with the new template
    dispatch(
      updateDrawerTemplate({
        isFirstDrawer: true,
        data: newTemplate,
        groupCreationMode: true,
      })
    );
  }, [dispatch]);
  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const Footer = useCallback(
    () => (
      <div className="footer-action">
        <Button size="small" type="primary" onClick={onClickAddNew}>
          {getText('create_new')}
        </Button>
      </div>
    ),
    [onClickAddNew]
  );

  const [confirmDelete, delContextHolder] = useDeleteConfirmation();

  const onRowClick = useCallback(
    (row) => {
      dispatch(
        updateDrawerTemplate({
          isFirstDrawer: true,
          data: row,
          groupCreationMode: false,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(fetchTemplates());
    dispatch(fetchMemberships());
    dispatch(fetchRelationTypes());
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
    dispatch(fetchActivityCreateTemplates());
  }, [dispatch]);
  const onRow = useRowProps(onRowClick, highlightIds);

  const onCompare = useCallback(
    (record) => {
      if (!firstDrawer) {
        return dispatch(
          updateDrawerTemplate({
            isFirstDrawer: true,
            data: record,
            groupCreationMode: false,
          })
        );
      }
      dispatch(
        updateDrawerTemplate({
          isFirstDrawer: false,
          data: record,
          groupCreationMode: false,
        })
      );
    },
    [dispatch, firstDrawer]
  );

  const onDelete = useCallback(
    (record) => {
      confirmDelete(record?.name, () => {
        dispatch(requestDeleteTemplate(record.id));
      });
    },
    [confirmDelete, dispatch]
  );

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(highlightIdsSelected);
    }, 100);
  }, [highlightIdsSelected.join('-')]);

  const menuProps = useCallback(
    (record) => {
      return getMenuProps({ record, onCompare, title: record.name, onDelete });
    },
    [onCompare, onDelete]
  );

  const [contentRef, defaultTableProps] = useDefaultTableProps();
  return (
    <DefaultContentLayout
      title={getText('object_generation_title')}
      subtitle={getText('object_generation_subtitle')}
    >
      <div className="inner-content--wrapper">
        <div ref={contentRef} style={{ margin: '10px 5px 0px', width: '100%' }}>
          <LoadingWrapper isLoading={loading}>
            <Table
              key={`table-${activityCreateTemplates.length}`}
              columns={columns}
              dataSource={templates}
              onRow={onRow}
              getMenuProps={menuProps}
              footer={Footer}
              {...defaultTableProps}
            />
          </LoadingWrapper>
        </div>
        <ObjectGenerationDrawer />
        {delContextHolder}
      </div>
    </DefaultContentLayout>
  );
};

export default ObjectGenerationPage;
